import React from "react";
import DecorComboboxBase, { IDecorOption } from "./DecorComboboxBase";
import { ZERO_GUID } from "../../configurator/cards/edgeKeys";

/**
 * ÉL-specifikus decor selector. A régi közös DecorSelectorInput
 * autoSelectFallback NÉLKÜLI ágának VÁLTOZATLAN viselkedése:
 *  - konkrét value-t SOSEM ír felül — a value csak átmenetileg hiányozhat az
 *    options-ből (pl. reload alatt), a helyességéért a szülő (EdgeListContent) felel;
 *  - fallback CSAK üres / ZERO_GUID value esetén;
 *  - compatible élek csoportként a lista tetején, elválasztóval.
 */
interface IEdgeDecorSelectorInputProps {
    value?: string;
    options: IDecorOption[];
    tooltip?: string;
    editable?: boolean;
    readOnly?: boolean;
    notFound?: boolean;
    /** Ha megadva és nem üres, ezek az ID-k kerülnek a lista tetejére, vizuálisan elválasztva. */
    compatibleIds?: string[];
    onChange: (newId: string | null) => void;
}

const EdgeDecorSelectorInput: React.FC<IEdgeDecorSelectorInputProps> = (props) => {
    const [selectedId, setSelectedId] = React.useState<string | null>(props.value || null);
    const [isFocused, setIsFocused] = React.useState(false);

    // Sync, ha a value kívülről változott (paramValue frissítés).
    React.useEffect(() => {
        setSelectedId(props.value || null);
    }, [props.value]);

    const compatibleSet = React.useMemo(() => {
        return new Set(props.compatibleIds?.length ? props.compatibleIds : []);
    }, [props.compatibleIds]);

    // A compatible ID-k SORRENDJE számít: a backend (CompatibleEdgings) struktúra-tudatosan
    // rendez — elöl a board SAJÁT dekor+struktúrájával is egyező élek, utánuk a többi
    // struktúra dekor-azonos éle. Ezt a sorrendet index-térképpel őrizzük meg.
    const compatibleOrder = React.useMemo(() => {
        const order = new Map<string, number>();
        (props.compatibleIds ?? []).forEach((id, index) => {
            if (!order.has(id)) order.set(id, index);
        });
        return order;
    }, [props.compatibleIds]);

    const hasCompatibleGroup = compatibleSet.size > 0;

    // Sort options: a compatible-k elöl, a backend (struktúra-tudatos) sorrendjében; utánuk a
    // többi, az options eredeti (név szerinti) sorrendjét megtartva (stabil rendezés → return 0).
    const sortedOptions = React.useMemo(() => {
        if (!hasCompatibleGroup) return props.options;
        return [...props.options].sort((a, b) => {
            const aOrder = compatibleOrder.has(a.id) ? compatibleOrder.get(a.id)! : Infinity;
            const bOrder = compatibleOrder.has(b.id) ? compatibleOrder.get(b.id)! : Infinity;
            if (aOrder !== bOrder) return aOrder - bOrder;
            return 0;
        });
    }, [props.options, compatibleOrder, hasCompatibleGroup]);

    const selectedOption = React.useMemo(() => {
        return props.options.find(o => o.id === selectedId) ?? null;
    }, [selectedId, props.options]);

    const lastFallbackRef = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (selectedOption !== null) {
            lastFallbackRef.current = null;
            return;
        }

        if (props.options.length === 0) return;

        // Szerkesztés közben (fókusz az inputban) nem fallback-elünk: hagyjuk a
        // felhasználót törölni / keresni. Kikattintáskor (blur) az effect újrafut.
        if (isFocused) return;

        // Ha van konkrét value (nem üres, nem ZERO_GUID), sose írunk felül — a value
        // csak átmenetileg hiányozhat az options-ből, a szülő felel a helyességéért.
        const hasConcreteValue = props.value && props.value !== ZERO_GUID;
        if (hasConcreteValue) {
            return;
        }

        const fallbackId = props.options[0].id;
        if (lastFallbackRef.current === fallbackId) return;

        lastFallbackRef.current = fallbackId;
        setSelectedId(fallbackId);
        props.onChange(fallbackId);
    }, [selectedOption, props.options, props.value, isFocused]);

    return (
        <DecorComboboxBase
            selectedOption={selectedOption}
            options={sortedOptions}
            compatibleSet={compatibleSet}
            tooltip={props.tooltip}
            editable={props.editable}
            readOnly={props.readOnly}
            notFound={props.notFound}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={(newId) => {
                setSelectedId(newId);
                props.onChange(newId);
            }}
        />
    );
};

export default EdgeDecorSelectorInput;